import { Component, ReactNode } from 'react';
import { AlertTriangle, RotateCw, Home } from 'lucide-react';
import { reportError } from '@/lib/sentry';

// App-wide crash fallback. Without this, any unhandled render exception
// leaves the user on a blank white screen with nothing to do. The
// boundary catches the error at the app root and shows a branded
// recovery page with two actions: reload (most common cause is a stale
// bundle after a deploy) or go back to the dashboard.
//
// We intentionally keep this simple — no error tracking SDK, no
// detailed stack trace in production. err?.message is enough to hint at
// the issue without leaking internals.

interface Props {
  children: ReactNode;
  /**
   * When set, a local child crash renders null instead of the
   * full-page fallback. Useful for wrapping a non-critical widget
   * (like OnboardingChecklist) so its crash doesn't take down the
   * page it's embedded in.
   */
  silent?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
     
    console.error('App crashed:', error, info.componentStack);
    // Forward to Sentry when configured — no-ops otherwise.
    reportError(error, { componentStack: info.componentStack });
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    // silent mode — widget crashed but we'd rather show nothing than
    // obscure the parent page with a full-screen error card.
    if (this.props.silent) return null;

    const message = this.state.error?.message || 'An unexpected error occurred';
    const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mb-5">
            <AlertTriangle className="h-7 w-7 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-500 mb-5">
            The page hit an unexpected error. Try reloading — if it keeps happening, head back to
            the dashboard and our team will look into it.
          </p>

          {isDev && (
            <pre className="text-[11px] text-left bg-slate-50 border border-slate-200 rounded-lg p-3 mb-5 overflow-auto max-h-40 text-red-600">
              {message}
            </pre>
          )}

          <div className="flex gap-2 justify-center">
            <button
              onClick={() => { this.reset(); window.location.reload(); }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold"
            >
              <RotateCw className="h-4 w-4" /> Reload page
            </button>
            <a
              href="/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              <Home className="h-4 w-4" /> Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
